/**
 * Agent Dashboard — derived selectors.
 *
 * Split out of agent_logs.ts to keep that file within the 300-line ceiling
 * (ST-01), following the same view/render split as viewingDeck.ts.
 *
 * Every function here is pure: it reads the event list passed to it and holds
 * no module state, so each can be unit-tested without booting the view.
 */

export interface LiveRun {
  runId: string
  kind: string
  title: string
  phase: string
}
export interface LiveEvent {
  type: string
  turn?: number
  think?: string
  text?: string
  tool?: string
  toolArgs?: string
  toolResult?: string
  toolError?: boolean
  usage?: string
}

export function getTodos(
  events: readonly LiveEvent[],
): { text: string; status: string; priority?: string }[] {
  for (let i = events.length - 1; i >= 0; i--) {
    const e = events[i]
    if (!e) continue
    if (e.type === 'tool' && (e.tool === 'TodoWrite' || e.tool === 'todo_write')) {
      try {
        const a = JSON.parse(e.toolArgs || '{}')
        if (a.todos) return a.todos as { text: string; status: string }[]
        if (a.tasks)
          return (a.tasks as { text: string; status?: string }[]).map((t) => ({
            text: t.text,
            status: t.status || 'pending',
          }))
      } catch {
        return []
      }
    }
  }
  return []
}
export function getSubagents(
  events: readonly LiveEvent[],
): { agent: string; task: string; model?: string }[] {
  const a: { agent: string; task: string; model?: string }[] = []
  for (const e of events) {
    if (e.type === 'tool' && e.tool === 'subagent') {
      try {
        const args = JSON.parse(e.toolArgs || '{}')
        if (args.agent)
          a.push({
            agent: args.agent as string,
            task: ((args.task as string) || '').slice(0, 200),
            model: args.model as string | undefined,
          })
        if (args.tasks)
          for (const t of args.tasks as { agent: string; task: string; model?: string }[])
            a.push({ agent: t.agent, task: (t.task || '').slice(0, 200), model: t.model })
      } catch {}
    }
  }
  return a
}
export function getAdvisorCalls(events: readonly LiveEvent[]): { q: string; r?: string }[] {
  const c: { q: string; r?: string }[] = []
  for (const e of events) {
    if (e.type === 'tool' && e.tool === 'advise') {
      try {
        const a = JSON.parse(e.toolArgs || '{}')
        c.push({ q: (a.question as string) || '' })
      } catch {
        c.push({ q: e.toolArgs || '' })
      }
    }
    if (e.type === 'result' && !e.toolError && c.length > 0) {
      const last = c[c.length - 1]
      if (last) last.r = e.toolResult
    }
  }
  return c
}
export function getTurn(events: readonly LiveEvent[]): number {
  for (let i = events.length - 1; i >= 0; i--) {
    const event = events[i]
    if (event?.type === 'turn') return event.turn || 0
  }
  return 0
}
export function getTokens(events: readonly LiveEvent[]): { i: number; o: number } {
  let i = 0,
    o = 0
  for (const e of events) {
    if (e.type === 'usage' && e.usage) {
      const mi = /↑(\d+\.?\d*)(k?)/.exec(e.usage),
        mo = /↓(\d+\.?\d*)(k?)/.exec(e.usage)
      if (mi) {
        let v = parseFloat(mi[1] ?? '0')
        if (mi[2] === 'k') v *= 1000
        i += v
      }
      if (mo) {
        let v = parseFloat(mo[1] ?? '0')
        if (mo[2] === 'k') v *= 1000
        o += v
      }
    }
  }
  return { i, o }
}
